import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

interface FileItem {
  id: string;
  name: string;
  mainCategory: string;
  subCategory: string;
  url: string;
  downloadUrl: string;
  version: number;
  totalVersions: number;
  createdAt: string;
}

interface Building {
  id: string;
  name: string;
  buildingType: string;
  yearBuilt: number;
  grossFloorArea: number;
  heatingType: string;
  description: string;
  navvisUrl: string;
  cafmUrl: string;
  plotId: string;
  plot: {
    id: string;
    name: string;
    project: { id: string; name: string }
  }
}

const MAIN_CATEGORY_LABEL: Record<string, string> = {
  POINTCLOUD: 'Punktwolken', MODEL_3D: '3D-Modelle',
  DRAWING_2D: '2D-Pläne', DOCUMENT: 'Dokumente', OTHER: 'Sonstiges',
}
const SUB_CATEGORY_LABEL: Record<string, string> = {
  SCAN_RAW: 'Scandaten', BIM: 'BIM-Modell', MESH: 'Mesh',
  FLOOR_PLAN: 'Grundriss', SECTION: 'Schnitt', ELEVATION: 'Ansicht',
  ORTHOPHOTO: 'Orthofoto', AREA_BOOK: 'Raumbuch / Flächenbuch',
  SITE_PLAN: 'Lageplan / Flurkarte', RESCUE_PLAN: 'Feuer- & Rettungsplan',
  PHOTO: 'Fotos', RENDERING: 'Renderings', VIDEO: 'Videos',
  AREA_CALC: 'Flächenauswertung', CONDITION: 'Gebäudezustandsbewertung',
  FIRE_SAFETY: 'Brandschutzkonzept', HAZMAT: 'Schadstoffsanierung', REPORT: 'Berichte',
}
const MAIN_CATEGORY_COLOR: Record<string, string> = {
  POINTCLOUD: '#e74c3c', MODEL_3D: '#3498db',
  DRAWING_2D: '#2ecc71', DOCUMENT: '#f39c12', OTHER: '#95a5a6',
}
const SUB_CATEGORIES_BY_MAIN: Record<string, string[]> = {
  POINTCLOUD: ['SCAN_RAW'], MODEL_3D: ['BIM', 'MESH'],
  DRAWING_2D: ['FLOOR_PLAN', 'SECTION', 'ELEVATION', 'ORTHOPHOTO', 'AREA_BOOK', 'SITE_PLAN', 'RESCUE_PLAN', 'PHOTO', 'RENDERING', 'VIDEO'],
  DOCUMENT: ['AREA_CALC', 'CONDITION', 'FIRE_SAFETY', 'HAZMAT', 'REPORT'], OTHER: [],
}
const CATEGORY_ORDER = ['POINTCLOUD', 'MODEL_3D', 'DRAWING_2D', 'DOCUMENT', 'OTHER']

const ALL_WIDGETS = [
  { id: 'vollstaendigkeit', label: 'Datenvollständigkeit' },
  { id: 'kategorien', label: 'Dateien nach Kategorie' },
  { id: 'metadaten', label: 'Gebäude-Metadaten' },
  { id: 'links', label: 'Externe Links' },
]
const DEFAULT_WIDGETS = ['vollstaendigkeit', 'kategorien', 'metadaten', 'links']

const getFileExt = (name: string) => name.split('.').pop()?.toLowerCase() || ''
const canPreview = (file: FileItem): string | null => {
  const ext = getFileExt(file.name)
  if (ext === 'pdf') return 'pdf'
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return 'image'
  if (['mp4', 'webm', 'mov'].includes(ext)) return 'video'
  if (['glb', 'gltf'].includes(ext)) return 'model'
  if (file.mainCategory === 'MODEL_3D' && file.subCategory === 'BIM') return 'ifc'
  return null
}

// ─── Storage URL Resolver ─────────────────────────────────────────────────────
// Das Backend gibt immer Production-URLs zurück (portalis.pointreef.cloud/storage/...).
// Lokal muss die URL auf MinIO (localhost:9000) umgebogen werden.
const resolveStorageUrl = (url: string): string => {
  if (process.env.NODE_ENV === 'development') {
    return url.replace(
      'https://portalis.pointreef.cloud/storage/portalis-files/',
      'http://localhost:9000/portalis-files/'
    )
  }
  return url
}

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 B'
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

const formatDuration = (seconds: number) => {
  if (seconds < 60) return '< 1 Min'
  if (seconds < 3600) return `${Math.round(seconds / 60)} Min`
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600)
    const m = Math.round((seconds % 3600) / 60)
    return m > 0 ? `${h}h ${m} Min` : `${h} Std`
  }
  return `${Math.floor(seconds / 86400)}d ${Math.round((seconds % 86400) / 3600)}h`
}

const SPEED_PROFILES = [
  { label: 'DSL 50 Mbps',        mbps: 50,   icon: '📡' },
  { label: 'VDSL 100 Mbps',      mbps: 100,  icon: '🌐' },
  { label: 'Glasfaser 250 Mbps', mbps: 250,  icon: '⚡' },
  { label: 'Glasfaser 1 Gbps',   mbps: 1000, icon: '🚀' }
]

const BuildingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [building, setBuilding] = useState<Building | null>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [expandedMain, setExpandedMain] = useState<Record<string, boolean>>({});
  const [expandedSub, setExpandedSub] = useState<Record<string, boolean>>({});
  const [selectedFileIds, setSelectedFileIds] = useState<Record<string, boolean>>({});
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [targetMainCat, setTargetMainCat] = useState('');
  const [targetSubCat, setTargetSubCat] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ file: FileItem; mode: 'single' | 'all' } | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [versionHistory, setVersionHistory] = useState<FileItem[] | null>(null);
  const [versionHistoryFile, setVersionHistoryFile] = useState<FileItem | null>(null);
  const [showWidgetConfig, setShowWidgetConfig] = useState(false);
  const [largeFileConfirm, setLargeFileConfirm] = useState<{file: File, storageStats: any | null, resolve: (b: boolean) => void} | null>(null);
  const [activeWidgets, setActiveWidgets] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('building_widgets');
      return saved ? JSON.parse(saved) : DEFAULT_WIDGETS;
    } catch { return DEFAULT_WIDGETS; }
  });
  const [uploadQueue, setUploadQueue] = useState<{
    id: string
    file: File
    status: 'queued' | 'awaiting' | 'uploading' | 'done' | 'error' | 'cancelled'
    percent: number
    uploadedParts: number
    totalParts: number
    partSize: number
    startTime?: number
    errorMsg?: string
    uploadId?: string
  }[]>([]);
  const abortControllers = useRef<Record<string, AbortController>>({});
  const [versionConfirm, setVersionConfirm] = useState<{
    file: File
    existingFile: any
    storageStats?: any
    resolve: (choice: 'version' | 'overwrite' | 'cancel') => void
  } | null>(null);
  const [batchConfirm, setBatchConfirm] = useState<{
    items: { itemId: string; file: File; existingFile: any | null }[]
    storageStats: any | null
    resolve: (decisions: { itemId: string; mode: 'new' | 'version' | 'overwrite' | 'skip' }[] | null) => void
  } | null>(null);
  
  const [batchDecisions, setBatchDecisions] = useState<Record<string, 'new' | 'version' | 'overwrite' | 'skip'>>({});
  
  const updateQueueItem = (id: string, update: any) => {
    setUploadQueue(q => q.map(item => item.id === id ? { ...item, ...update } : item))
  };


  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canUpload = !['CUSTOMER_VIEWER'].includes(user.role);
  const canDelete = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN', 'CUSTOMER_MANAGER'].includes(user.role);
  const selectedCount = Object.values(selectedFileIds).filter(Boolean).length;

  const fetchBuilding = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/buildings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuilding(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/files/building/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchBuilding(); fetchFiles(); }, [id]); // eslint-disable-line

  // ─── Widget config ─────────────────────────────────────────────────────────
  const toggleWidget = (wid: string) => {
    const updated = activeWidgets.includes(wid)
      ? activeWidgets.filter(w => w !== wid)
      : [...activeWidgets, wid];
    setActiveWidgets(updated);
    localStorage.setItem('building_widgets', JSON.stringify(updated));
  };

  // ─── Statistics ─────────────────────────────────────────────────────────────
  const catCounts = files.reduce((acc, f) => {
    acc[f.mainCategory] = (acc[f.mainCategory] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const completeness = [
    { key: 'POINTCLOUD', label: 'Punktwolke', color: '#e74c3c' },
    { key: 'MODEL_3D', label: '3D-Modell', color: '#3498db' },
    { key: 'DRAWING_2D', label: '2D-Pläne', color: '#2ecc71' },
    { key: 'DOCUMENT', label: 'Dokumente', color: '#f39c12' },
  ].map(c => ({ ...c, has: (catCounts[c.key] || 0) > 0, count: catCounts[c.key] || 0 }));

  const completenessScore = Math.round((completeness.filter(c => c.has).length / completeness.length) * 100);

  // ─── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    await processFiles(Array.from(selectedFiles));
  };

  const processFiles = async (fileArray: File[]) => {
    if (fileArray.length === 0) return
    
    const items = fileArray.map(f => ({
      id: Math.random().toString(36).slice(2),
      file: f,
      status: 'queued' as const,
      percent: 0, uploadedParts: 0, totalParts: 0, partSize: 0
    }))
    setUploadQueue(items)
    setUploading(true)
    
    // Phase 1: Existence-Checks für alle Dateien
    const checkResults: { itemId: string; file: File; existingFile: any | null }[] = []
    for (const item of items) {
      updateQueueItem(item.id, { status: 'awaiting' })
      try {
        const r = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/files/check`,
          { name: item.file.name, buildingId: id },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        checkResults.push({ itemId: item.id, file: item.file, existingFile: r.data.exists ? r.data.file : null })
      } catch {
        checkResults.push({ itemId: item.id, file: item.file, existingFile: null })
      }
    }
    
    // Phase 2: Batch-Entscheidungs-Dialog (wenn Konflikte oder große Dateien)
    const needsDecision = checkResults.some(r => r.existingFile !== null || r.file.size > 1024 * 1024 * 1024)
    let modeMap = new Map<string, 'new' | 'version' | 'overwrite' | 'skip'>()
    
    if (needsDecision) {
      let storageStats: any = null
      try {
        const r = await axios.get(`${process.env.REACT_APP_API_URL}/api/files/chunked/storage-stats`, { headers: { Authorization: `Bearer ${token}` } })
        storageStats = r.data
      } catch {}
      
      // Defaults setzen
      const initialDecisions: Record<string, 'new' | 'version' | 'overwrite' | 'skip'> = {}
      checkResults.forEach(r => {
        initialDecisions[r.itemId] = r.existingFile ? 'version' : 'new'
      })
      setBatchDecisions(initialDecisions)
      
      const decisions = await new Promise<{ itemId: string; mode: 'new' | 'version' | 'overwrite' | 'skip' }[] | null>(resolve => {
        setBatchConfirm({ items: checkResults, storageStats, resolve })
      })
      setBatchConfirm(null)
      setBatchDecisions({})
      
      if (!decisions) {
        items.forEach(i => updateQueueItem(i.id, { status: 'cancelled' }))
        setUploading(false)
        setTimeout(() => setUploadQueue([]), 3000)
        return
      }
      
      decisions.forEach(d => modeMap.set(d.itemId, d.mode))
    } else {
      checkResults.forEach(r => modeMap.set(r.itemId, 'new'))
    }
    
    // Phase 3: Alle Uploads ohne Unterbrechung
    for (const item of items) {
      const mode = modeMap.get(item.id)
      if (!mode || mode === 'skip') {
        updateQueueItem(item.id, { status: 'cancelled' })
        continue
      }
      try {
        updateQueueItem(item.id, { status: 'uploading', startTime: Date.now() })
        await uploadSingleFile(item.id, item.file, mode as 'new' | 'version' | 'overwrite')
        updateQueueItem(item.id, { status: 'done', percent: 100 })
      } catch (err: any) {
        if (err.message !== 'UPLOAD_CANCELLED') {
          updateQueueItem(item.id, { status: 'error', errorMsg: err.message })
        }
      }
    }
    
    fetchFiles()
    setUploading(false)
    setTimeout(() => setUploadQueue([]), 3000)
  };
  
  const applyBulkDecision = (mode: 'version' | 'overwrite' | 'skip') => {
    if (!batchConfirm) return
    const newDecisions = { ...batchDecisions }
    batchConfirm.items.forEach(it => {
      if (it.existingFile) newDecisions[it.itemId] = mode
    })
    setBatchDecisions(newDecisions)
  };

  const uploadSingleFile = async (itemId: string, file: File, mode: 'new' | 'version' | 'overwrite') => {
    const overwrite = mode === 'overwrite'
    const apiUrl = process.env.REACT_APP_API_URL
    const headers = { Authorization: `Bearer ${token}` }
    
    const controller = new AbortController()
    abortControllers.current[itemId] = controller
    
    const startResp = await axios.post(`${apiUrl}/api/files/chunked/start`, {
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || 'application/octet-stream',
      buildingId: id,
      overwrite
    }, { headers })
    
    const { uploadId, partSize, totalParts, partUrls } = startResp.data
    updateQueueItem(itemId, { totalParts, partSize, uploadId })
    
    let completed = 0
    let next = 0
    
    const worker = async () => {
      while (next < totalParts) {
        if (controller.signal.aborted) return
        const partIndex = next++
        const partNumber = partIndex + 1
        const blob = file.slice(partIndex * partSize, Math.min((partIndex + 1) * partSize, file.size))
        const url = partUrls[partIndex]
        
        let lastErr: any = null
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const r = await fetch(url, { method: 'PUT', body: blob, signal: controller.signal })
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            lastErr = null
            break
          } catch (err: any) {
            if (err.name === 'AbortError') throw err
            lastErr = err
            await new Promise(r => setTimeout(r, 1000 * (attempt + 1)))
          }
        }
        if (lastErr) throw new Error(`Part ${partNumber}: ${lastErr.message}`)
        
        completed++
        updateQueueItem(itemId, { uploadedParts: completed, percent: Math.round((completed / totalParts) * 100) })
      }
    }
    
    try {
      await Promise.all([worker(), worker(), worker(), worker(), worker()])
      await axios.post(`${apiUrl}/api/files/chunked/${uploadId}/complete`, {}, { headers })
    } catch (err: any) {
      if (err.name === 'AbortError') {
        try { await axios.delete(`${apiUrl}/api/files/chunked/${uploadId}`, { headers }) } catch {}
        throw new Error('UPLOAD_CANCELLED')
      }
      throw err
    } finally {
      delete abortControllers.current[itemId]
    }
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const url = deleteConfirm.mode === 'all'
        ? `${process.env.REACT_APP_API_URL}/api/files/${deleteConfirm.file.id}/all-versions`
        : `${process.env.REACT_APP_API_URL}/api/files/${deleteConfirm.file.id}`;
      await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } });
      setDeleteConfirm(null);
      setVersionHistory(null);
      setVersionHistoryFile(null);
      fetchFiles();
    } catch (err) { console.error(err); }
  };

  const handleMoveFiles = async () => {
    if (!targetMainCat) return;
    const ids = Object.keys(selectedFileIds).filter(k => selectedFileIds[k]);
    try {
      await Promise.all(ids.map(fileId =>
        axios.patch(`${process.env.REACT_APP_API_URL}/api/files/${fileId}/category`,
          { mainCategory: targetMainCat, subCategory: targetSubCat || null },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ));
      setSelectedFileIds({});
      setShowMoveModal(false);
      setTargetMainCat('');
      setTargetSubCat('');
      fetchFiles();
    } catch (err) { console.error(err); }
  };

  const loadVersionHistory = async (file: FileItem) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/files/${file.id}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersionHistory(res.data);
      setVersionHistoryFile(file);
    } catch (err) { console.error(err); }
  };

  const toggleMain = (cat: string) => setExpandedMain(prev => ({ ...prev, [cat]: !prev[cat] }));
  const toggleSub = (key: string) => setExpandedSub(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleFile = (fileId: string) => setSelectedFileIds(prev => ({ ...prev, [fileId]: !prev[fileId] }));

  const grouped = files.reduce((acc, file) => {
    const main = file.mainCategory || 'OTHER';
    const sub = file.subCategory || 'OTHER';
    if (!acc[main]) acc[main] = {};
    if (!acc[main][sub]) acc[main][sub] = [];
    acc[main][sub].push(file);
    return acc;
  }, {} as Record<string, Record<string, FileItem[]>>);

  const sortedMain = Object.keys(grouped).sort((a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b));
  const previewType = previewFile ? canPreview(previewFile) : null;

  if (!building) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#888' }}>Laden...</div>
  );

  return (
    <div style={s.container}>
      <div style={s.sidebar}>
        <div style={{ padding: '0 16px 24px 16px', textAlign: 'center' }}>
          <img src="/portalis_logo.png" alt="Portalis" style={{ width: '160px' }} />
        </div>
        <nav style={s.nav}>
          <a href="/dashboard" style={s.navItem}>Startseite</a>
          <a href="/projects" style={s.navItemActive}>Liegenschaften</a>
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN'].includes(user.role) && (
            <a href="/users" style={s.navItem}>Benutzer</a>
          )}
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(user.role) && (
            <a href="/customers" style={s.navItem}>Kunden</a>
          )}
        </nav>
        <div style={s.userInfo}>
          <p style={s.userName}>{user.firstName} {user.lastName}</p>
          <p style={s.userRole}>{user.role}</p>
          <button style={s.logoutButton} onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>Abmelden</button>
        </div>
      </div>

      <div style={s.main}>
        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <a href="/projects" style={s.breadcrumbLink}>Liegenschaften</a>
          {building.plot?.project && (
            <><span style={s.breadcrumbSep}> › </span>
            <a href={`/projects/${building.plot.project.id}`} style={s.breadcrumbLink}>{building.plot.project.name}</a></>
          )}
          {building.plot && (
            <><span style={s.breadcrumbSep}> › </span>
            <span style={{ ...s.breadcrumbLink, cursor: 'pointer' }} onClick={() => window.history.back()}>{building.plot.name}</span></>
          )}
          <span style={s.breadcrumbSep}> › </span>
          <span style={s.breadcrumbCurrent}>{building.name}</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.title}>{building.name}</h1>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
              {building.buildingType && <span style={s.metaTag}>{building.buildingType}</span>}
              {building.yearBuilt && <span style={s.metaTag}>Baujahr {building.yearBuilt}</span>}
              {building.grossFloorArea && <span style={s.metaTag}>{building.grossFloorArea.toLocaleString('de-DE')} m² BGF</span>}
              {building.heatingType && <span style={s.metaTag}>{building.heatingType}</span>}
            </div>
            {building.description && <p style={s.description}>{building.description}</p>}
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={s.statCard}>
              <span style={s.statNumber}>{files.length}</span>
              <span style={s.statLabel}>Dateien</span>
            </div>
            <button style={s.configBtn} onClick={() => setShowWidgetConfig(!showWidgetConfig)} title="Widgets konfigurieren">
              ⚙️
            </button>
          </div>
        </div>

        {/* Widget config */}
        {showWidgetConfig && (
          <div style={s.widgetConfigPanel}>
            <p style={{ margin: '0 0 10px 0', fontWeight: '600', fontSize: '13px', color: '#0f2744' }}>Sichtbare Widgets</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {ALL_WIDGETS.map(w => (
                <button key={w.id} onClick={() => toggleWidget(w.id)} style={{
                  padding: '5px 12px', borderRadius: '20px', border: '1px solid',
                  borderColor: activeWidgets.includes(w.id) ? '#00c49a' : '#ddd',
                  backgroundColor: activeWidgets.includes(w.id) ? '#f0faf7' : 'white',
                  color: activeWidgets.includes(w.id) ? '#00c49a' : '#888',
                  cursor: 'pointer', fontSize: '12px', fontWeight: '500'
                }}>
                  {activeWidgets.includes(w.id) ? '✓ ' : ''}{w.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Widgets */}
        <div style={s.widgetRow}>

          {/* Datenvollständigkeit */}
          {activeWidgets.includes('vollstaendigkeit') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Datenvollständigkeit</p>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', color: '#666' }}>Gesamt</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: completenessScore === 100 ? '#00c49a' : '#f39c12' }}>
                    {completenessScore}%
                  </span>
                </div>
                <div style={{ backgroundColor: '#f0f0f0', borderRadius: '4px', height: '8px' }}>
                  <div style={{
                    width: `${completenessScore}%`,
                    backgroundColor: completenessScore === 100 ? '#00c49a' : completenessScore >= 50 ? '#f39c12' : '#e74c3c',
                    borderRadius: '4px', height: '8px', transition: 'width 0.3s'
                  }} />
                </div>
              </div>
              {completeness.map(c => (
                <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%',
                    backgroundColor: c.has ? c.color : '#f0f0f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {c.has && <span style={{ color: 'white', fontSize: '11px' }}>✓</span>}
                  </div>
                  <span style={{ fontSize: '12px', color: c.has ? '#333' : '#aaa', flex: 1 }}>{c.label}</span>
                  {c.has && <span style={{ fontSize: '11px', color: '#888' }}>{c.count}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Kategorien */}
          {activeWidgets.includes('kategorien') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Dateien nach Kategorie</p>
              {files.length === 0 ? (
                <p style={{ color: '#aaa', fontSize: '13px' }}>Noch keine Dateien</p>
              ) : (
                Object.entries(catCounts).map(([cat, count]) => (
                  <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#666', width: '80px', flexShrink: 0 }}>{MAIN_CATEGORY_LABEL[cat] || cat}</span>
                    <div style={{ flex: 1, backgroundColor: '#f0f0f0', borderRadius: '4px', height: '8px' }}>
                      <div style={{
                        width: `${(count / files.length) * 100}%`,
                        backgroundColor: MAIN_CATEGORY_COLOR[cat] || '#95a5a6',
                        borderRadius: '4px', height: '8px'
                      }} />
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#333', width: '20px', textAlign: 'right' }}>{count}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Metadaten */}
          {activeWidgets.includes('metadaten') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Gebäude-Metadaten</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { label: 'Typ', value: building.buildingType },
                  { label: 'Baujahr', value: building.yearBuilt },
                  { label: 'BGF', value: building.grossFloorArea ? `${building.grossFloorArea.toLocaleString('de-DE')} m²` : null },
                  { label: 'Heizung', value: building.heatingType },
                ].map(item => item.value ? (
                  <div key={item.label} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#888', width: '55px', flexShrink: 0 }}>{item.label}</span>
                    <span style={{ fontSize: '12px', color: '#333', fontWeight: '500' }}>{item.value}</span>
                  </div>
                ) : null)}
                {!building.buildingType && !building.yearBuilt && !building.grossFloorArea && !building.heatingType && (
                  <p style={{ color: '#aaa', fontSize: '13px', margin: 0 }}>Keine Metadaten hinterlegt</p>
                )}
              </div>
            </div>
          )}

          {/* Externe Links */}
          {activeWidgets.includes('links') && (
            <div style={s.widget}>
              <p style={s.widgetTitle}>Externe Links</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {building.navvisUrl ? (
                  <a href={building.navvisUrl} target="_blank" rel="noreferrer" style={s.externalLink}>
                    <span style={s.externalLinkIcon}>🔍</span>
                    NavVis Viewer öffnen
                  </a>
                ) : (
                  <p style={{ color: '#aaa', fontSize: '12px', margin: 0 }}>Kein NavVis Link hinterlegt</p>
                )}
                {building.cafmUrl ? (
                  <a href={building.cafmUrl} target="_blank" rel="noreferrer" style={s.externalLink}>
                    <span style={s.externalLinkIcon}>🏗️</span>
                    CAFM öffnen
                  </a>
                ) : (
                  <p style={{ color: '#aaa', fontSize: '12px', margin: 0 }}>Kein CAFM Link hinterlegt</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Upload */}
        {canUpload && (
          <div
            style={{ ...s.dropzone, borderColor: dragOver ? '#00c49a' : '#ddd', backgroundColor: dragOver ? '#f0faf7' : 'white' }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => handleUpload(e.target.files)} />
            {uploadQueue.length > 0 ? (
              <div style={{ width: '100%', maxWidth: '700px', margin: '0 auto', textAlign: 'left' }}>
                {uploadQueue.map(item => {
                  const elapsed = item.startTime ? (Date.now() - item.startTime) / 1000 : 0
                  const bytesUploaded = item.uploadedParts * item.partSize
                  const speedBps = elapsed > 0 ? bytesUploaded / elapsed : 0
                  const remainingBytes = item.file.size - bytesUploaded
                  const etaSec = speedBps > 0 ? remainingBytes / speedBps : 0
                  
                  const statusLabel = {
                    queued: '⏳ Wartend',
                    awaiting: '❓ Warte auf Bestätigung…',
                    uploading: `${item.percent}%`,
                    done: '✓ Fertig',
                    error: '✕ Fehler',
                    cancelled: '⊘ Abgebrochen'
                  }[item.status]
                  
                  const barColor = {
                    queued: '#aaa', awaiting: '#f39c12', uploading: '#00c49a',
                    done: '#00c49a', error: '#e74c3c', cancelled: '#aaa'
                  }[item.status]
                  
                  return (
                    <div key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ color: '#0f2744', fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '12px' }}>
                          {item.file.name}
                        </span>
                        <span style={{ color: '#888', fontSize: '11px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {formatBytes(item.file.size)} · {statusLabel}
                          {(item.status === 'uploading' || item.status === 'queued') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                if (abortControllers.current[item.id]) {
                                  abortControllers.current[item.id].abort()
                                }
                                updateQueueItem(item.id, { status: 'cancelled' })
                              }}
                              style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '14px', padding: '0 2px', lineHeight: 1 }}
                              title="Upload abbrechen"
                            >✕</button>
                          )}
                        </span>
                      </div>
                      <div style={{ backgroundColor: '#f0f0f0', borderRadius: '3px', height: '6px', overflow: 'hidden' }}>
                        <div style={{ width: `${item.percent}%`, backgroundColor: barColor, height: '6px', transition: 'width 0.3s' }} />
                      </div>
                      {item.status === 'uploading' && speedBps > 0 && (
                        <p style={{ margin: '4px 0 0 0', color: '#888', fontSize: '11px' }}>
                          {item.uploadedParts}/{item.totalParts} Parts · {formatBytes(speedBps)}/s · noch {formatDuration(etaSec)}
                        </p>
                      )}
                      {item.status === 'error' && item.errorMsg && (
                        <p style={{ margin: '4px 0 0 0', color: '#e74c3c', fontSize: '11px' }}>{item.errorMsg}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <>
                <p style={{ fontSize: '36px', margin: '0 0 6px 0' }}>📁</p>
                <p style={{ color: '#555', margin: '0 0 4px 0', fontSize: '14px' }}>Dateien hier hineinziehen oder klicken</p>
                <p style={{ color: '#aaa', margin: '0', fontSize: '12px' }}>Kategorisierung erfolgt automatisch</p>
              </>
            )}
          </div>
        )}

        {/* Selection bar */}
        {selectedCount > 0 && (
          <div style={s.selectionBar}>
            <span style={{ color: 'white', fontSize: '13px' }}>{selectedCount} Datei(en) ausgewählt</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setSelectedFileIds({})} style={s.selectionCancelBtn}>Aufheben</button>
              <button onClick={() => setShowMoveModal(true)} style={s.selectionMoveBtn}>Verschieben</button>
            </div>
          </div>
        )}

        {/* File list */}
        {sortedMain.length === 0 ? (
          <div style={s.empty}>
            <p style={{ fontSize: '36px', margin: '0 0 8px 0' }}>📂</p>
            <p style={{ color: '#aaa', margin: '0' }}>Noch keine Dateien hochgeladen</p>
          </div>
        ) : (
          sortedMain.map(mainCat => (
            <div key={mainCat} style={s.categoryCard}>
              <div style={s.categoryHeader} onClick={() => toggleMain(mainCat)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={s.categoryToggle}>{expandedMain[mainCat] ? '▼' : '▶'}</span>
                  <span style={{ ...s.categoryBadge, backgroundColor: MAIN_CATEGORY_COLOR[mainCat] || '#95a5a6' }}>
                    {MAIN_CATEGORY_LABEL[mainCat] || mainCat}
                  </span>
                  <span style={s.categoryCount}>
                    {Object.values(grouped[mainCat]).reduce((sum, arr) => sum + arr.length, 0)} Datei(en)
                  </span>
                </div>
              </div>
              {expandedMain[mainCat] && Object.entries(grouped[mainCat]).map(([subCat, subFiles]) => {
                const subKey = `${mainCat}-${subCat}`;
                return (
                  <div key={subCat} style={s.subCategory}>
                    <div style={{ ...s.subCategoryHeader, cursor: 'pointer' }} onClick={() => toggleSub(subKey)}>
                      <span style={s.subCategoryTitle}>
                        <span style={{ color: '#aaa', fontSize: '10px', marginRight: '6px' }}>{expandedSub[subKey] ? '▼' : '▶'}</span>
                        {SUB_CATEGORY_LABEL[subCat] || subCat}
                      </span>
                      <span style={s.subCount}>{subFiles.length}</span>
                    </div>
                    {expandedSub[subKey] && subFiles.map(file => {
                      const preview = canPreview(file);
                      const hasVersions = file.totalVersions > 1;
                      return (
                        <div key={file.id} style={s.fileItem}>
                          <input type="checkbox" checked={!!selectedFileIds[file.id]}
                            onChange={() => toggleFile(file.id)} onClick={e => e.stopPropagation()}
                            style={{ accentColor: '#00c49a', flexShrink: 0 }} />
                          <div style={s.fileInfo}>
                            <p style={s.fileName}>{file.name}</p>
                            <p style={s.fileMeta}>
                              {new Date(file.createdAt).toLocaleDateString('de-DE')}
                              {hasVersions && <span style={s.versionBadge}>v{file.version}</span>}
                              {hasVersions && (
                                <span style={s.versionsLink} onClick={() => loadVersionHistory(file)}>
                                  Versionen ({file.totalVersions})
                                </span>
                              )}
                            </p>
                          </div>
                          <div style={s.fileActions}>
                            {preview && (
                              <button
                                onClick={() => {
                                  if (preview === 'ifc') {
                                    // URL lokal auf MinIO umbiegen, auf Production unverändert
                                    const resolvedUrl = resolveStorageUrl(file.downloadUrl)
                                    navigate(`/viewer?url=${encodeURIComponent(resolvedUrl)}`)
                                  } else {
                                    setPreviewFile(file)
                                  }
                                }}
                                style={s.viewBtn}
                              >
                                Öffnen
                              </button>
                            )}
                            <a href={resolveStorageUrl(file.downloadUrl)} target="_blank" rel="noreferrer" style={s.downloadBtn}>Download</a>
                            {canDelete && (
                              <button onClick={() => setDeleteConfirm({ file, mode: 'single' })} style={s.deleteBtn}>🗑️</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Preview Modal */}
      {previewFile && previewType && previewType !== 'ifc' && (
        <div style={s.modalOverlay} onClick={() => setPreviewFile(null)}>
          <div style={s.previewModal} onClick={e => e.stopPropagation()}>
            <div style={s.previewHeader}>
              <span style={{ color: 'white', fontSize: '14px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewFile.name}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <a href={resolveStorageUrl(previewFile.downloadUrl)} target="_blank" rel="noreferrer" style={s.previewDownloadBtn}>Download</a>
                <button onClick={() => setPreviewFile(null)} style={s.previewCloseBtn}>✕</button>
              </div>
            </div>
            <div style={s.previewContent}>
              {previewType === 'pdf' && <iframe src={resolveStorageUrl(previewFile.downloadUrl)} style={{ width: '100%', height: '100%', border: 'none' }} title={previewFile.name} />}
              {previewType === 'image' && <img src={resolveStorageUrl(previewFile.downloadUrl)} alt={previewFile.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
              {previewType === 'video' && <video controls style={{ maxWidth: '100%', maxHeight: '100%' }}><source src={resolveStorageUrl(previewFile.downloadUrl)} /></video>}
              {previewType === 'model' && (
                // @ts-ignore
                <model-viewer src={resolveStorageUrl(previewFile.downloadUrl)} alt={previewFile.name} auto-rotate camera-controls style={{ width: '100%', height: '100%' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 16px 0' }}>{selectedCount} Datei(en) verschieben</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px', textAlign: 'left' }}>
              <div>
                <label style={s.modalLabel}>Hauptkategorie</label>
                <select style={s.modalSelect} value={targetMainCat} onChange={e => { setTargetMainCat(e.target.value); setTargetSubCat(''); }}>
                  <option value="">Bitte wählen...</option>
                  {Object.keys(MAIN_CATEGORY_LABEL).map(cat => <option key={cat} value={cat}>{MAIN_CATEGORY_LABEL[cat]}</option>)}
                </select>
              </div>
              {targetMainCat && (SUB_CATEGORIES_BY_MAIN[targetMainCat] || []).length > 0 && (
                <div>
                  <label style={s.modalLabel}>Unterkategorie <span style={{ color: '#aaa', fontWeight: 'normal' }}>(optional)</span></label>
                  <select style={s.modalSelect} value={targetSubCat} onChange={e => setTargetSubCat(e.target.value)}>
                    <option value="">Bitte wählen...</option>
                    {SUB_CATEGORIES_BY_MAIN[targetMainCat].map(sub => <option key={sub} value={sub}>{SUB_CATEGORY_LABEL[sub]}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => setShowMoveModal(false)} style={s.cancelBtn}>Abbrechen</button>
              <button onClick={handleMoveFiles} disabled={!targetMainCat} style={{ ...s.confirmMoveBtn, opacity: targetMainCat ? 1 : 0.5 }}>Verschieben</button>
            </div>
          </div>
        </div>
      )}



      {/* Version History */}
      {versionHistory && versionHistoryFile && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, maxWidth: '540px' }}>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 4px 0' }}>Versionshistorie</p>
            <p style={{ color: '#888', fontSize: '13px', margin: '0 0 20px 0' }}>{versionHistoryFile.name}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', textAlign: 'left' }}>
              {versionHistory.map(v => (
                <div key={v.id} style={{
                  display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '8px',
                  backgroundColor: v.id === versionHistoryFile.id ? '#f0faf7' : '#f9f9f9',
                  border: v.id === versionHistoryFile.id ? '1px solid #00c49a' : '1px solid #f0f0f0'
                }}>
                  <span style={{
                    backgroundColor: v.id === versionHistoryFile.id ? '#00c49a' : '#f0f0f0',
                    color: v.id === versionHistoryFile.id ? 'white' : '#888',
                    padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', flexShrink: 0
                  }}>v{v.version}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>
                      {new Date(v.createdAt).toLocaleDateString('de-DE')} {new Date(v.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {v.id === versionHistoryFile.id && <span style={{ fontSize: '11px', color: '#00c49a', fontWeight: '600' }}>Aktuell</span>}
                  <a href={resolveStorageUrl(v.downloadUrl)} target="_blank" rel="noreferrer" style={s.downloadBtn}>Download</a>
                  {canDelete && <button onClick={() => setDeleteConfirm({ file: v, mode: 'single' })} style={s.deleteBtn}>🗑️</button>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => { setVersionHistory(null); setVersionHistoryFile(null); }} style={s.cancelBtn}>Schließen</button>
              {canDelete && <button onClick={() => setDeleteConfirm({ file: versionHistoryFile, mode: 'all' })} style={s.confirmDeleteBtn}>Alle Versionen löschen</button>}
            </div>
          </div>
        </div>
      )}

      {/* Batch Confirm */}
      {batchConfirm && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, maxWidth: '720px', maxHeight: '85vh', overflow: 'auto' }}>
            <p style={{ fontSize: '20px', fontWeight: 600, color: '#0f2744', margin: '0 0 8px 0', textAlign: 'left' }}>
              Upload-Bestätigung
            </p>
            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 16px 0', textAlign: 'left' }}>
              Triff alle Entscheidungen jetzt – der Upload läuft danach ohne Unterbrechung durch.
            </p>
            
            {/* Bulk-Aktionen */}
            {batchConfirm.items.filter(i => i.existingFile).length > 1 && (
              <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', marginBottom: '12px', textAlign: 'left', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', color: '#475569' }}>Alle Konflikte:</span>
                <button onClick={() => applyBulkDecision('version')} style={{ padding: '4px 10px', fontSize: '12px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>Neue Version</button>
                <button onClick={() => applyBulkDecision('overwrite')} style={{ padding: '4px 10px', fontSize: '12px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>Überschreiben</button>
                <button onClick={() => applyBulkDecision('skip')} style={{ padding: '4px 10px', fontSize: '12px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>Überspringen</button>
              </div>
            )}
            
            {/* Datei-Liste */}
            <div style={{ marginBottom: '16px', textAlign: 'left' }}>
              {batchConfirm.items.map(item => {
                const decision = batchDecisions[item.itemId] || (item.existingFile ? 'version' : 'new')
                const isLarge = item.file.size > 1024 * 1024 * 1024
                
                return (
                  <div key={item.itemId} style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid #eee',
                    background: decision === 'skip' ? '#fafafa' : 'white',
                    opacity: decision === 'skip' ? 0.6 : 1
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 600, color: '#0f2744', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                        {item.file.name}
                      </span>
                      <span style={{ color: '#888', fontSize: '12px' }}>
                        {formatBytes(item.file.size)}{isLarge && ' 🔥'}
                      </span>
                    </div>
                    
                    {item.existingFile ? (
                      <>
                        <p style={{ fontSize: '11px', color: '#999', margin: '0 0 6px 0' }}>
                          V{item.existingFile.version} existiert{item.existingFile.size && <> ({formatBytes(item.existingFile.size)})</>}
                        </p>
                        <div style={{ display: 'flex', gap: '14px', fontSize: '12px' }}>
                          <label style={{ cursor: 'pointer' }}>
                            <input type="radio" checked={decision === 'version'} onChange={() => setBatchDecisions(d => ({ ...d, [item.itemId]: 'version' }))} />
                            {' '}Als V{item.existingFile.version + 1}
                          </label>
                          <label style={{ cursor: 'pointer' }}>
                            <input type="radio" checked={decision === 'overwrite'} onChange={() => setBatchDecisions(d => ({ ...d, [item.itemId]: 'overwrite' }))} />
                            {' '}Überschreiben
                          </label>
                          <label style={{ cursor: 'pointer' }}>
                            <input type="radio" checked={decision === 'skip'} onChange={() => setBatchDecisions(d => ({ ...d, [item.itemId]: 'skip' }))} />
                            {' '}Überspringen
                          </label>
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', gap: '14px', fontSize: '12px' }}>
                        <label style={{ cursor: 'pointer' }}>
                          <input type="radio" checked={decision === 'new'} onChange={() => setBatchDecisions(d => ({ ...d, [item.itemId]: 'new' }))} />
                          {' '}Hochladen
                        </label>
                        <label style={{ cursor: 'pointer' }}>
                          <input type="radio" checked={decision === 'skip'} onChange={() => setBatchDecisions(d => ({ ...d, [item.itemId]: 'skip' }))} />
                          {' '}Überspringen
                        </label>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            
            {/* Zusammenfassung */}
            {(() => {
              const willUpload = batchConfirm.items.filter(it => {
                const d = batchDecisions[it.itemId] || (it.existingFile ? 'version' : 'new')
                return d !== 'skip'
              })
              const totalBytes = willUpload.reduce((sum, it) => sum + it.file.size, 0)
              const stats = batchConfirm.storageStats
              const afterBytes = stats ? stats.customerUsedBytes + totalBytes : 0
              const usagePercent = stats ? Math.round((afterBytes / stats.serverTotalBytes) * 100) : 0
              const isWarning = usagePercent > 75
              
              return (
                <div style={{ background: isWarning ? '#fff7ed' : '#f8fafc', borderLeft: isWarning ? '3px solid #f97316' : 'none', padding: '12px', borderRadius: '6px', textAlign: 'left' }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#475569' }}>
                    <strong>{willUpload.length}</strong> Dateien · <strong>{formatBytes(totalBytes)}</strong> gesamt
                  </p>
                  {totalBytes > 100 * 1024 * 1024 && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                      Dauer (250 Mbps): ca. {formatDuration((totalBytes * 1.15) / ((250 * 1000000) / 8))}
                    </p>
                  )}
                  {stats && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: isWarning ? '#78350f' : '#666' }}>
                      Speicher nach Upload: {formatBytes(afterBytes)} / {formatBytes(stats.serverTotalBytes)} ({usagePercent}%)
                      {isWarning && ' ⚠️'}
                    </p>
                  )}
                </div>
              )
            })()}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => batchConfirm.resolve(null)} style={s.cancelBtn}>
                Alles abbrechen
              </button>
              <button onClick={() => {
                const finalDecisions = batchConfirm.items.map(it => ({
                  itemId: it.itemId,
                  mode: batchDecisions[it.itemId] || (it.existingFile ? 'version' as const : 'new' as const)
                }))
                batchConfirm.resolve(finalDecisions)
              }} style={s.confirmMoveBtn}>
                Alle hochladen
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirm */}
      {deleteConfirm && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 8px 0' }}>
              {deleteConfirm.mode === 'all' ? 'Alle Versionen löschen' : 'Version löschen'}
            </p>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 4px 0' }}><strong>{deleteConfirm.file.name}</strong></p>
            <p style={{ color: '#999', fontSize: '13px', margin: '0 0 24px 0' }}>
              {deleteConfirm.mode === 'all' ? 'Alle Versionen werden unwiderruflich gelöscht.'
                : deleteConfirm.file.version > 1 ? `Version ${deleteConfirm.file.version} wird gelöscht. Die vorherige Version wird wieder aktuell.`
                : 'Diese Datei wird unwiderruflich gelöscht.'}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)} style={s.cancelBtn}>Abbrechen</button>
              <button onClick={handleDelete} style={s.confirmDeleteBtn}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' },
  sidebar: { width: '220px', backgroundColor: '#0f2744', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#aac4e0', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', textDecoration: 'none', display: 'block' },
  navItemActive: { color: 'white', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', backgroundColor: '#1e3a5f', borderLeft: '3px solid #00c49a', textDecoration: 'none', display: 'block' },
  userInfo: { padding: '24px', borderTop: '1px solid #1e3a5f' },
  userName: { color: 'white', margin: '0', fontSize: '14px', fontWeight: '600' },
  userRole: { color: '#00c49a', margin: '4px 0 12px 0', fontSize: '12px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid #aac4e0', color: '#aac4e0', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%' },
  main: { flex: 1, padding: '24px 32px', overflow: 'auto' },
  breadcrumb: { marginBottom: '16px', fontSize: '14px' },
  breadcrumbLink: { color: '#00c49a', textDecoration: 'none' },
  breadcrumbSep: { color: '#aaa', margin: '0 8px' },
  breadcrumbCurrent: { color: '#666' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '16px' },
  title: { fontSize: '26px', color: '#0f2744', margin: '0 0 4px 0' },
  metaTag: { backgroundColor: '#f0f0f0', color: '#666', padding: '3px 10px', borderRadius: '20px', fontSize: '12px' },
  description: { color: '#888', margin: '8px 0 0 0', fontSize: '14px' },
  statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '12px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flexShrink: 0 },
  statNumber: { fontSize: '28px', fontWeight: 'bold', color: '#00c49a' },
  statLabel: { fontSize: '12px', color: '#888' },
  configBtn: { backgroundColor: 'white', border: '1px solid #ddd', color: '#666', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' },
  widgetConfigPanel: { backgroundColor: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '12px' },
  widgetRow: { display: 'flex', gap: '12px', overflowX: 'auto', marginBottom: '16px', paddingBottom: '4px' },
  widget: { backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minWidth: '200px', maxWidth: '260px', flexShrink: 0 },
  widgetTitle: { margin: '0 0 12px 0', fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  externalLink: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f0faf7', borderRadius: '8px', textDecoration: 'none', color: '#00c49a', fontSize: '13px', fontWeight: '500' },
  externalLinkIcon: { fontSize: '16px' },
  dropzone: { border: '2px dashed', borderRadius: '12px', padding: '28px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px', transition: 'all 0.2s' },
  empty: { textAlign: 'center', padding: '48px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  selectionBar: { position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#0f2744', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  selectionCancelBtn: { padding: '5px 12px', borderRadius: '6px', border: '1px solid #aac4e0', backgroundColor: 'transparent', color: '#aac4e0', cursor: 'pointer', fontSize: '12px' },
  selectionMoveBtn: { padding: '5px 12px', borderRadius: '6px', border: 'none', backgroundColor: '#00c49a', color: 'white', cursor: 'pointer', fontSize: '12px' },
  categoryCard: { backgroundColor: 'white', borderRadius: '12px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', cursor: 'pointer' },
  categoryToggle: { color: '#aaa', fontSize: '11px' },
  categoryBadge: { color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' },
  categoryCount: { color: '#888', fontSize: '13px' },
  subCategory: { borderTop: '1px solid #f0f0f0' },
  subCategoryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px', backgroundColor: '#fafafa' },
  subCategoryTitle: { color: '#555', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center' },
  subCount: { backgroundColor: '#f0f0f0', color: '#888', padding: '1px 8px', borderRadius: '10px', fontSize: '11px' },
  fileItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderTop: '1px solid #fafafa' },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: { margin: '0', color: '#333', fontSize: '14px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileMeta: { margin: '2px 0 0 0', color: '#aaa', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' },
  versionBadge: { backgroundColor: '#f0f4ff', color: '#3498db', padding: '1px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' },
  versionsLink: { cursor: 'pointer', color: '#3498db', fontSize: '11px', textDecoration: 'underline' },
  fileActions: { display: 'flex', gap: '6px', flexShrink: 0 },
  viewBtn: { border: '1px solid #3498db', color: '#3498db', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', backgroundColor: 'transparent', cursor: 'pointer' },
  downloadBtn: { border: '1px solid #00c49a', color: '#00c49a', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' },
  deleteBtn: { border: '1px solid #e74c3c', color: '#e74c3c', padding: '5px 8px', borderRadius: '6px', fontSize: '12px', backgroundColor: 'transparent', cursor: 'pointer' },
  previewModal: { backgroundColor: '#1a1a1a', borderRadius: '12px', width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  previewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: '#0f0f0f', gap: '12px' },
  previewContent: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '8px' },
  previewDownloadBtn: { border: '1px solid #aac4e0', color: '#aac4e0', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', textDecoration: 'none' },
  previewCloseBtn: { border: 'none', background: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', padding: '0 4px' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '90%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalLabel: { fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '6px' },
  modalSelect: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit' },
  cancelBtn: { padding: '8px 20px', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px' },
  confirmDeleteBtn: { padding: '8px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#e74c3c', color: 'white', cursor: 'pointer', fontSize: '14px' },
  confirmMoveBtn: { padding: '8px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#00c49a', color: 'white', cursor: 'pointer', fontSize: '14px' },
};

export default BuildingDetail;
